import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Save,
  Loader2,
  Star,
  ListTree,
  Languages,
  ChevronDown,
  ChevronUp,
  X,
  Asterisk,
  Eye,
  EyeOff,
  HelpCircle,
  Sparkles,
  Briefcase,
  UserRound,
  Users,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Admin UI for the Setup Chat flows.
 * Backend:
 *   GET  /api/setup-chat/admin/flows
 *   POST /api/setup-chat/admin/flows
 *   PUT  /api/setup-chat/admin/flows/{id}
 *   POST /api/setup-chat/admin/flows/{id}/translate
 *   DELETE /api/setup-chat/admin/flows/{id}
 *
 * Service-type matching uses the live list from /service-types/list — admin
 * picks names from a multi-select instead of typing keywords.
 */
const TRANSLATABLE_LANGS = [
  { code: "uk", label: "Ukrainian" },
  { code: "ru", label: "Russian" },
];

// Languages the editor can render the question list in. EN is the canonical
// source; everything else is translation overlay.
const VIEW_LANGS = [
  { code: "en", label: "English (source)" },
  { code: "uk", label: "Ukrainian" },
  { code: "ru", label: "Russian" },
];

// Pull what to *display* for a question in a given language. EN reads
// top-level; UK/RU reads from translations[code], falling back to EN.
function viewOf(q, lang) {
  if (lang === "en") return {
    section: q.section || "",
    label: q.label || "",
    message: q.message || "",
    placeholder: q.placeholder || "",
    options: Array.isArray(q.options) ? q.options : [],
    isFallback: false,
  };
  const tr = (q.translations || {})[lang] || {};
  return {
    section: tr.section || q.section || "",
    label: tr.label || q.label || "",
    message: tr.message || q.message || "",
    placeholder: tr.placeholder || q.placeholder || "",
    options: Array.isArray(tr.options) && tr.options.length
      ? tr.options
      : (Array.isArray(q.options) ? q.options : []),
    // Whether the displayed value is FROM English fallback (no translation yet)
    isFallback: !tr.message,
  };
}

// Build a patch onto the right place: EN writes top-level, UK/RU writes
// to translations[lang][key].
function buildLangPatch(q, lang, patch) {
  if (lang === "en") return patch;
  const cur = q.translations || {};
  const prev = cur[lang] || {};
  const next = { ...prev };
  for (const [k, v] of Object.entries(patch)) {
    if (k === "options") {
      next.options = v;
    } else {
      next[k] = v;
    }
  }
  return { translations: { ...cur, [lang]: next } };
}

export default function SetupFlowsAdminPage() {
  const [flows, setFlows] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([
    "text", "textarea", "yesno", "select", "url", "file",
  ]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [activeFlowId, setActiveFlowId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [expanded, setExpanded] = useState({}); // {idx: boolean} — which rows show details
  const [showNewFlow, setShowNewFlow] = useState(false);
  const [newFlow, setNewFlow] = useState({ id: "", name: "" });
  const [showTrDialog, setShowTrDialog] = useState({ open: false, idx: null, code: null });
  // Which language the editor is currently rendering. EN = source, UK/RU = translation overlay.
  const [viewLang, setViewLang] = useState("en");

  const reload = async () => {
    setLoading(true);
    try {
      const [flowsRes, stRes] = await Promise.all([
        api.get("/setup-chat/admin/flows"),
        api.get("/service-types/list").catch(() => ({ data: [] })),
      ]);
      setFlows(flowsRes.data.flows || []);
      setQuestionTypes(flowsRes.data.question_types || questionTypes);
      setServiceTypes(stRes.data || []);
      if (!activeFlowId && flowsRes.data.flows?.length) {
        setActiveFlowId(flowsRes.data.flows[0].id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load flows");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeFlowId) {
      setDraft(null);
      return;
    }
    const found = flows.find((f) => f.id === activeFlowId);
    if (!found) {
      setDraft(null);
      return;
    }
    setDraft({
      ...found,
      service_type_match: Array.isArray(found.service_type_match) ? [...found.service_type_match] : [],
      questions: (found.questions || []).map((q) => ({
        ...q,
        translations: q.translations ? { ...q.translations } : {},
      })),
    });
    setExpanded({});
  }, [activeFlowId, flows]);

  const dirty = useMemo(() => {
    if (!draft) return false;
    const orig = flows.find((f) => f.id === activeFlowId);
    if (!orig) return true;
    if ((orig.name || "") !== (draft.name || "")) return true;
    if ((orig.is_default || false) !== (draft.is_default || false)) return true;
    const a = (orig.service_type_match || []).slice().sort().join("|");
    const b = (draft.service_type_match || []).slice().sort().join("|");
    if (a !== b) return true;
    if ((orig.questions || []).length !== draft.questions.length) return true;
    for (let i = 0; i < draft.questions.length; i++) {
      const x = draft.questions[i];
      const y = orig.questions[i] || {};
      // `owner` and `ai_validate` belong here too — without them the dirty
      // check misses a row where only that control changed and Save stays
      // greyed out, silently dropping the edit.
      const keys = [
        "field", "section", "type", "label", "message", "placeholder",
        "required", "active", "owner", "ai_validate", "priority", "can_improvise",
      ];
      for (const k of keys) {
        if ((x[k] ?? "") !== (y[k] ?? "")) return true;
      }
      if (JSON.stringify(x.options || null) !== JSON.stringify(y.options || null)) return true;
      if (JSON.stringify(x.translations || {}) !== JSON.stringify(y.translations || {})) return true;
    }
    return false;
  }, [draft, flows, activeFlowId]);

  const sectionsList = useMemo(() => {
    if (!draft) return [];
    const seen = new Set();
    draft.questions.forEach((q) => seen.add((q.section || "General").trim()));
    return Array.from(seen);
  }, [draft]);

  const updateQuestion = (idx, patch) => {
    setDraft((d) => {
      if (!d) return d;
      const next = [...d.questions];
      next[idx] = { ...next[idx], ...patch };
      return { ...d, questions: next };
    });
  };

  const moveQuestion = (idx, dir) => {
    setDraft((d) => {
      if (!d) return d;
      const target = idx + dir;
      if (target < 0 || target >= d.questions.length) return d;
      const next = [...d.questions];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...d, questions: next.map((q, i) => ({ ...q, position: i })) };
    });
  };

  const removeQuestion = (idx) => {
    if (!window.confirm("Remove this question?")) return;
    setDraft((d) => {
      if (!d) return d;
      const next = d.questions.filter((_, i) => i !== idx);
      return { ...d, questions: next.map((q, i) => ({ ...q, position: i })) };
    });
  };

  const addQuestion = () => {
    setDraft((d) => {
      if (!d) return d;
      const lastSection = d.questions[d.questions.length - 1]?.section || "General";
      const next = [
        ...d.questions,
        {
          field: `field_${Date.now().toString(36)}`,
          section: lastSection,
          type: "text",
          label: "",
          message: "",
          placeholder: "",
          options: null,
          required: false,
          // Default to non-blocking: a new question should never freeze the
          // sales → PM-pool handoff until someone deliberately marks it.
          owner: "any",
          priority: "helpful",
          can_improvise: false,
          active: true,
          position: d.questions.length,
          translations: {},
        },
      ];
      return { ...d, questions: next };
    });
    // Auto-expand the new row
    setExpanded((e) => ({ ...e, [draft?.questions.length || 0]: true }));
  };

  const buildSavePayload = (d) => ({
    name: d.name,
    is_default: !!d.is_default,
    service_type_match: (d.service_type_match || []).filter(Boolean),
    questions: d.questions.map((q, i) => ({
      ...q,
      position: i,
      options:
        q.type === "select"
          ? Array.isArray(q.options)
            ? q.options
            : String(q.options || "").split("\n").map((s) => s.trim()).filter(Boolean)
          : null,
    })),
  });

  const saveFlow = async () => {
    if (!draft) return;
    for (const q of draft.questions) {
      if (!q.field?.trim()) {
        toast.error("Every question needs a field key");
        return;
      }
      if (!q.message?.trim()) {
        toast.error(`Question ${q.field} is missing a message`);
        return;
      }
      if (q.type === "select" && (!q.options || q.options.length === 0)) {
        toast.error(`Select question ${q.field} needs at least one option`);
        return;
      }
    }
    setSaving(true);
    try {
      await api.put(`/setup-chat/admin/flows/${draft.id}`, buildSavePayload(draft));
      toast.success("Flow saved");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const generateTranslations = async () => {
    if (!draft) return;
    if (dirty) {
      if (!window.confirm("You have unsaved changes — translations will use the LAST SAVED version. Save now?")) return;
      try {
        await api.put(`/setup-chat/admin/flows/${draft.id}`, buildSavePayload(draft));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not save before translating");
        return;
      }
    }
    setTranslating(true);
    try {
      const { data } = await api.post(`/setup-chat/admin/flows/${draft.id}/translate`, {
        languages: TRANSLATABLE_LANGS.map((l) => l.code),
      });
      const sum = data._summary || {};
      const langs = (sum.languages || []).map((l) => `${l.code}:${l.translated_count}`).join(", ");
      toast.success(`Translations generated (${langs})`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Translation failed");
    } finally {
      setTranslating(false);
    }
  };

  const createFlow = async () => {
    const slug = (newFlow.id || "").trim().toLowerCase();
    if (!slug || !/^[a-z0-9_]+$/.test(slug)) {
      toast.error("Flow id: lowercase letters, digits, underscore only");
      return;
    }
    try {
      await api.post("/setup-chat/admin/flows", {
        id: slug,
        name: newFlow.name?.trim() || slug,
        service_type_match: [],
        questions: [
          {
            field: "starterField",
            section: "General",
            type: "text",
            label: "First question",
            message: "Replace me with your first question",
            required: false,
            owner: "any",
            active: true,
            position: 0,
          },
        ],
      });
      setShowNewFlow(false);
      setNewFlow({ id: "", name: "" });
      setActiveFlowId(slug);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create flow");
    }
  };

  const deleteFlow = async () => {
    if (!draft) return;
    if (!window.confirm(`Delete flow "${draft.name}"?`)) return;
    try {
      await api.delete(`/setup-chat/admin/flows/${draft.id}`);
      toast.success("Flow deleted");
      setActiveFlowId(null);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const trCounts = useMemo(() => {
    if (!draft) return {};
    const counts = {};
    TRANSLATABLE_LANGS.forEach((l) => {
      counts[l.code] = draft.questions.filter((q) => q.translations?.[l.code]?.message).length;
    });
    return counts;
  }, [draft]);

  return (
    <div className="space-y-4 p-1">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <ListTree className="h-6 w-6 text-cyan-400" />
            Setup chat flows
          </h1>
          <p className="text-sm text-zinc-400">
            Question sets for the AI onboarding chat. Pick a flow on the left and edit questions on the right.
          </p>
        </div>
        <Button onClick={() => setShowNewFlow(true)} className="bg-cyan-600 hover:bg-cyan-500 text-white">
          <Plus className="h-4 w-4 mr-1" /> New flow
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
        {/* Flow list */}
        <Card className="bg-zinc-900/50 border-zinc-800 self-start">
          <CardContent className="p-2">
            {loading && flows.length === 0 ? (
              <div className="text-sm text-zinc-500 px-2 py-3 flex items-center">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
              </div>
            ) : (
              <ul className="space-y-1">
                {flows.map((f) => (
                  <li key={f.id}>
                    <button
                      onClick={() => setActiveFlowId(f.id)}
                      className={
                        "w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between gap-2 " +
                        (activeFlowId === f.id
                          ? "bg-cyan-950/40 border border-cyan-800/50 text-zinc-50"
                          : "hover:bg-zinc-800/50 text-zinc-300 border border-transparent")
                      }
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate flex items-center gap-1">
                          {f.is_default && <Star className="h-3 w-3 text-amber-400 flex-shrink-0" />}
                          {f.name}
                        </div>
                        <div className="text-[10px] text-zinc-500 font-mono truncate">
                          {f.id} · {(f.questions || []).length}q
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Editor */}
        {!draft ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-8 text-center text-zinc-500 text-sm">
              Pick a flow on the left, or create a new one.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {/* Flow meta */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-3 space-y-3">
                <div className="flex items-end gap-3 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <Label className="text-[10px] uppercase tracking-wide text-zinc-500">Display name</Label>
                    <Input
                      value={draft.name}
                      onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                      className="h-8 bg-zinc-950 border-zinc-700 text-zinc-50"
                    />
                  </div>
                  <div className="w-32">
                    <Label className="text-[10px] uppercase tracking-wide text-zinc-500">Slug</Label>
                    <Input value={draft.id} readOnly disabled className="h-8 bg-zinc-950 border-zinc-700 text-zinc-500 font-mono text-xs" />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-zinc-300 h-8">
                    <Switch
                      checked={!!draft.is_default}
                      onCheckedChange={(v) => setDraft((d) => ({ ...d, is_default: v }))}
                    />
                    Default fallback
                  </label>
                  <div className="flex items-center gap-2 ml-auto">
                    {dirty && (
                      <Badge variant="outline" className="border-amber-500/50 text-amber-400 text-[10px]">
                        unsaved
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={generateTranslations}
                      disabled={translating || !draft.questions.length}
                      className="h-8 text-xs text-violet-300 hover:text-violet-200 border border-violet-600/30 hover:border-violet-500/60"
                      title="Generate UK + RU translations from English source via GPT-4o"
                    >
                      {translating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Languages className="h-3.5 w-3.5 mr-1" />}
                      Generate translations
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={deleteFlow}
                      className="h-8 text-xs text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                    </Button>
                    <Button
                      size="sm"
                      onClick={saveFlow}
                      disabled={!dirty || saving}
                      className="h-8 bg-cyan-600 hover:bg-cyan-500 text-white"
                    >
                      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                      Save
                    </Button>
                  </div>
                </div>

                <ServiceTypePicker
                  selected={draft.service_type_match}
                  options={serviceTypes}
                  onChange={(v) => setDraft((d) => ({ ...d, service_type_match: v }))}
                />

                {/* View-language tabs + translation status */}
                <div className="flex items-center gap-2 text-[11px] text-zinc-500 pt-1 border-t border-zinc-800">
                  <div className="flex items-center gap-0 bg-zinc-950 border border-zinc-700 rounded overflow-hidden">
                    {VIEW_LANGS.map((l) => {
                      const isActive = viewLang === l.code;
                      const cnt = l.code === "en"
                        ? draft.questions.length
                        : (trCounts[l.code] || 0);
                      const total = draft.questions.length;
                      return (
                        <button
                          key={l.code}
                          type="button"
                          onClick={() => setViewLang(l.code)}
                          className={
                            "px-3 py-1 text-xs font-medium uppercase " +
                            (isActive
                              ? "bg-cyan-600 text-white"
                              : "text-zinc-300 hover:bg-zinc-800")
                          }
                          title={`Show questions as ${l.label}`}
                        >
                          {l.code} <span className="opacity-60 ml-1">{cnt}/{total}</span>
                        </button>
                      );
                    })}
                  </div>
                  {viewLang !== "en" && (
                    <span className="text-[10px] text-amber-400 italic">
                      Editing {viewLang.toUpperCase()} translations · empty fields fall back to English at runtime
                    </span>
                  )}
                  {draft.translations_updated_at && (
                    <span className="ml-auto">translations last refreshed {String(draft.translations_updated_at).slice(0, 10)}</span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Questions */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="py-2 px-3 border-b border-zinc-800">
                <CardTitle className="text-sm text-zinc-300 flex items-center justify-between">
                  <span>Questions ({draft.questions.length})</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={addQuestion}
                    className="h-7 text-xs text-cyan-400 hover:text-cyan-300"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add question
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                {/* Column legend */}
                <div className="text-[10px] text-zinc-500 px-2 pb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <HelpCircle className="h-3 w-3 flex-shrink-0" />
                  <span>
                    <span className="text-zinc-400">Section</span> — sidebar group
                  </span>
                  <span>
                    <span className="text-zinc-400">field_key</span> — DB key (no spaces)
                  </span>
                  <span>
                    <span className="text-zinc-400">type</span> — input kind
                  </span>
                  <span>
                    <span className="text-zinc-400">Short label</span> — sidebar caption
                  </span>
                  <span className="flex items-center gap-1">
                    <Asterisk className="h-3 w-3 text-red-400" />
                    <span className="text-zinc-400">Req</span> — must be answered
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="h-3 w-3 text-cyan-400" />
                    <span className="text-zinc-400">Show</span> — visible to clients
                  </span>
                  <span className="flex items-center gap-1">
                    <Sparkles className="h-3 w-3 text-violet-400" />
                    <span className="text-zinc-400">AI</span> — bot validates answers before saving
                  </span>
                  <span className="flex items-center gap-1">
                    <Briefcase className="h-3 w-3 text-amber-400" />
                    <span className="text-zinc-400">Manager / Client / Either</span> — who answers
                    (amber border = Req + Manager: blocks the project from reaching the PM pool, can't be skipped)
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    <span className="text-zinc-400">Essential / Helpful / Optional</span> — client priority (the more from the top, the faster)
                  </span>
                  <span className="flex items-center gap-1">
                    <Wand2 className="h-3 w-3 text-violet-400" />
                    <span className="text-zinc-400">Improvise</span> — studio can produce it; PM won't chase the client
                  </span>
                  <span>
                    <span className="text-zinc-400">UK / RU</span> — translation status (click to edit)
                  </span>
                </div>
                {draft.questions.length === 0 ? (
                  <div className="text-zinc-500 text-sm p-4 text-center">No questions. Click "Add question" to start.</div>
                ) : (
                  <div className="space-y-1">
                    {draft.questions.map((q, idx) => (
                      <CompactQuestionRow
                        key={idx}
                        q={q}
                        idx={idx}
                        types={questionTypes}
                        sectionsList={sectionsList}
                        expanded={!!expanded[idx]}
                        viewLang={viewLang}
                        onToggle={() => setExpanded((e) => ({ ...e, [idx]: !e[idx] }))}
                        onChange={(patch) => updateQuestion(idx, patch)}
                        onChangeLocalized={(patch) =>
                          updateQuestion(idx, buildLangPatch(q, viewLang, patch))
                        }
                        onUp={() => moveQuestion(idx, -1)}
                        onDown={() => moveQuestion(idx, +1)}
                        onRemove={() => removeQuestion(idx)}
                        onEditTranslation={(code) => setShowTrDialog({ open: true, idx, code })}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* New flow dialog */}
      <Dialog open={showNewFlow} onOpenChange={setShowNewFlow}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle>Create a new flow</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-400">Slug (e.g. video_tour)</Label>
              <Input
                value={newFlow.id}
                onChange={(e) => setNewFlow((n) => ({ ...n, id: e.target.value }))}
                className="bg-zinc-950 border-zinc-700 text-zinc-50 font-mono"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Display name</Label>
              <Input
                value={newFlow.name}
                onChange={(e) => setNewFlow((n) => ({ ...n, name: e.target.value }))}
                className="bg-zinc-950 border-zinc-700 text-zinc-50"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewFlow(false)}>Cancel</Button>
            <Button onClick={createFlow} className="bg-cyan-600 hover:bg-cyan-500 text-white">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Translation editor */}
      {showTrDialog.open && draft && (
        <TranslationDialog
          q={draft.questions[showTrDialog.idx]}
          code={showTrDialog.code}
          onClose={() => setShowTrDialog({ open: false, idx: null, code: null })}
          onSave={(payload) => {
            const idx = showTrDialog.idx;
            const code = showTrDialog.code;
            updateQuestion(idx, {
              translations: {
                ...(draft.questions[idx].translations || {}),
                [code]: payload,
              },
            });
            setShowTrDialog({ open: false, idx: null, code: null });
          }}
        />
      )}
    </div>
  );
}

// ── Service-type multi-select using /service-types/list ───────────────────

function ServiceTypePicker({ selected, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const toggle = (name) => {
    const lower = name.toLowerCase();
    if (selected.some((s) => s.toLowerCase() === lower)) {
      onChange(selected.filter((s) => s.toLowerCase() !== lower));
    } else {
      onChange([...selected, lower]);
    }
  };

  const selectedSet = new Set(selected.map((s) => s.toLowerCase()));

  return (
    <div ref={ref} className="relative">
      <Label className="text-[10px] uppercase tracking-wide text-zinc-500">
        Match these service types ({selected.length})
      </Label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-950 border border-zinc-700 rounded text-sm text-zinc-50 hover:border-cyan-500"
      >
        <span className="flex flex-wrap gap-1 min-h-[20px]">
          {selected.length === 0 ? (
            <span className="text-zinc-500">Click to pick service types…</span>
          ) : (
            selected.map((s) => (
              <span
                key={s}
                className="bg-cyan-900/40 border border-cyan-700/50 rounded px-1.5 py-0.5 text-xs text-cyan-200 flex items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(s);
                }}
              >
                {s} <X className="h-3 w-3" />
              </span>
            ))
          )}
        </span>
        <ChevronDown className="h-4 w-4 text-zinc-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-20 left-0 right-0 top-[calc(100%+2px)] max-h-80 overflow-y-auto bg-zinc-900 border border-zinc-700 rounded shadow-xl">
          {options.length === 0 ? (
            <div className="text-zinc-500 text-sm px-3 py-4">
              No service types in /service-types — manage them in Settings.
            </div>
          ) : (
            options.map((st) => {
              const checked = selectedSet.has(st.name?.toLowerCase());
              return (
                <button
                  key={st.id || st.name}
                  type="button"
                  onClick={() => toggle(st.name)}
                  className={
                    "w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-zinc-800 " +
                    (checked ? "bg-cyan-950/40 text-zinc-50" : "text-zinc-300")
                  }
                >
                  <span
                    className={
                      "h-3.5 w-3.5 rounded border flex items-center justify-center " +
                      (checked ? "bg-cyan-500 border-cyan-500" : "border-zinc-600")
                    }
                  >
                    {checked && <span className="text-[9px] text-zinc-50">✓</span>}
                  </span>
                  <span className="flex-1 truncate">{st.name}</span>
                </button>
              );
            })
          )}
        </div>
      )}
      <div className="text-[10px] text-zinc-500 mt-1">
        Substring match, case-insensitive — picking "Website" also routes services like "Website (Coming soon)".
      </div>
    </div>
  );
}

// ── Compact question row ──────────────────────────────────────────────────

// Who is expected to answer a question. Only `sales` has teeth: a required
// sales-owned question blocks the project from reaching the PM pool and cannot
// be skipped in the chat. `client` / `any` are collected after handoff and
// never gate production — see backend/setup_questions.py.
const OWNER_OPTS = [
  {
    value: "sales",
    label: "Manager",
    Icon: Briefcase,
    color: "text-amber-400",
    hint: "The sales manager must answer this before the project can go to the PM pool. Cannot be skipped. Use for what the deal itself tells you — scope, contact, commercial choices.",
  },
  {
    value: "client",
    label: "Client",
    Icon: UserRound,
    color: "text-sky-400",
    hint: "Only the client can realistically answer (their files, brand assets, accesses). Collected after handoff — never blocks the pool, and the manager may skip it.",
  },
  {
    value: "any",
    label: "Either",
    Icon: Users,
    color: "text-zinc-400",
    hint: "Either side may answer. Never blocks the pool; skippable.",
  },
];

// Client-facing priority tier. For client/any fields the "required" model is
// replaced by a gradient — the client gives what they have, top-down, and the
// more from the top the faster the build. Only shown for non-gating fields.
const PRIORITY_OPTS = [
  { value: "essential", label: "Essential", color: "text-emerald-400", hint: "Top priority — strongly speeds up the build." },
  { value: "helpful", label: "Helpful", color: "text-sky-400", hint: "Improves the result." },
  { value: "optional", label: "Optional", color: "text-zinc-400", hint: "Nice to have — fine to skip, add later." },
];

function PriorityPicker({ q, onChange, disabled }) {
  // Gating (required + Manager) fields aren't on the gradient — they're the hard
  // sales must-haves, so hide the tier there to avoid a contradictory control.
  const isGate = q.owner === "sales" && !!q.required;
  if (isGate) return <div className="w-[92px] flex-shrink-0" />;
  const priority = PRIORITY_OPTS.some((o) => o.value === q.priority) ? q.priority : "helpful";
  const current = PRIORITY_OPTS.find((o) => o.value === priority);
  return (
    <div className="px-1" title={disabled ? "Switch to EN tab to change" : current.hint}>
      <Select value={priority} onValueChange={(v) => onChange({ priority: v })} disabled={disabled}>
        <SelectTrigger className="h-6 w-[92px] px-1.5 text-[10px] bg-zinc-950 border-zinc-700 text-zinc-50 flex-shrink-0">
          <span className="flex items-center gap-1 min-w-0">
            <span className={"h-1.5 w-1.5 rounded-full flex-shrink-0 " + current.color.replace("text-", "bg-")} />
            <span className="truncate">{current.label}</span>
          </span>
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-700">
          {PRIORITY_OPTS.map((o) => (
            <SelectItem key={o.value} value={o.value} className="text-xs text-zinc-100">
              <span className="flex items-center gap-1.5">
                <span className={"h-1.5 w-1.5 rounded-full " + o.color.replace("text-", "bg-")} />
                {o.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ImproviseToggle({ q, onChange, disabled }) {
  const isGate = q.owner === "sales" && !!q.required;
  if (isGate) return <div className="w-[26px] flex-shrink-0" />;
  const on = !!q.can_improvise;
  return (
    <label
      className="flex items-center gap-1 text-[10px] text-zinc-300 px-1 flex-shrink-0"
      title={
        disabled
          ? "Switch to EN tab to change"
          : on
          ? "Studio can produce this if the client doesn't have it — the PM won't chase for it."
          : "Client must provide this — studio can't improvise it."
      }
    >
      <Switch checked={on} onCheckedChange={(v) => onChange({ can_improvise: v })} disabled={disabled} className="scale-75" />
      <Wand2 className={"h-3 w-3 " + (on ? "text-violet-400" : "text-zinc-500")} />
    </label>
  );
}

function OwnerPicker({ q, onChange, disabled }) {
  const owner = OWNER_OPTS.some((o) => o.value === q.owner) ? q.owner : "any";
  const current = OWNER_OPTS.find((o) => o.value === owner);
  const gates = owner === "sales" && !!q.required;
  return (
    <div
      className="px-1"
      title={
        disabled
          ? "Owner is language-independent — switch to EN tab to change"
          : current.hint +
            (gates
              ? "\n\n⚠ Required + Manager — this question currently BLOCKS the sales → PM-pool handoff."
              : "")
      }
    >
      <Select value={owner} onValueChange={(v) => onChange({ owner: v })} disabled={disabled}>
        {/* An amber border (not another icon) marks a gating question — a third
            glyph in this trigger wraps it onto two lines at this row height. */}
        <SelectTrigger
          className={
            "h-6 w-[96px] px-1.5 text-[10px] bg-zinc-950 text-zinc-50 flex-shrink-0 " +
            (gates ? "border-amber-500/60" : "border-zinc-700")
          }
        >
          <span className="flex items-center gap-1 min-w-0">
            <current.Icon className={"h-3 w-3 flex-shrink-0 " + current.color} />
            <span className="truncate">{current.label}</span>
          </span>
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-700">
          {OWNER_OPTS.map((o) => (
            <SelectItem key={o.value} value={o.value} className="text-xs text-zinc-100">
              <span className="flex items-center gap-1.5">
                <o.Icon className={"h-3 w-3 " + o.color} />
                {o.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CompactQuestionRow({
  q, idx, types, sectionsList, expanded, viewLang,
  onToggle, onChange, onChangeLocalized,
  onUp, onDown, onRemove, onEditTranslation,
}) {
  // What we render per language. EN → top-level fields. UK/RU → translations
  // overlay with English fallback.
  const view = viewOf(q, viewLang);
  const isLocalized = viewLang !== "en";

  // Localized writes for translatable fields (section/label/message/placeholder/options).
  const writeT = (patch) => onChangeLocalized(patch);

  const trBadges = TRANSLATABLE_LANGS.map((l) => {
    const has = !!(q.translations?.[l.code]?.message);
    const isCurrent = viewLang === l.code;
    return (
      <button
        key={l.code}
        type="button"
        onClick={(e) => { e.stopPropagation(); onEditTranslation(l.code); }}
        title={has ? `Edit ${l.label} translation in dialog` : `No ${l.label} translation — add via dialog or Generate translations`}
        className={
          "px-1.5 py-0.5 text-[9px] rounded border " +
          (isCurrent
            ? "border-cyan-500/60 text-cyan-300 bg-cyan-950/30"
            : has
            ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-950/40"
            : "border-zinc-700 text-zinc-500 hover:border-zinc-500")
        }
      >
        {l.code.toUpperCase()}
      </button>
    );
  });

  // Fallback indicator dot for translatable fields
  const FallbackDot = ({ visible }) =>
    visible ? (
      <span className="text-amber-400 text-[10px]" title="Falls back to English (no translation yet)">
        ↩
      </span>
    ) : null;

  return (
    <div className={
      "border rounded " +
      (isLocalized ? "border-cyan-900/40 bg-cyan-950/10" : "border-zinc-800 bg-zinc-950/30")
    }>
      {/* Compact top line — always visible */}
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <button
          type="button"
          onClick={onToggle}
          className="text-zinc-500 hover:text-zinc-300"
          title={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        <div className="flex items-center gap-0.5 w-[140px] flex-shrink-0 relative">
          <Input
            value={view.section}
            onChange={(e) => writeT({ section: e.target.value })}
            list={`sections-${idx}`}
            placeholder={isLocalized ? `${viewLang.toUpperCase()} section` : "Section"}
            title="Section — group label shown in the chat sidebar (e.g. Project Foundation)"
            className="h-7 px-2 text-xs bg-zinc-950 border-zinc-700 text-zinc-50 w-full"
          />
          <FallbackDot visible={isLocalized && view.isFallback && !!view.section} />
          <datalist id={`sections-${idx}`}>
            {sectionsList.map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
        <Input
          value={q.field || ""}
          onChange={(e) => onChange({ field: e.target.value })}
          placeholder="field_key"
          disabled={isLocalized}
          title={
            isLocalized
              ? "Field keys are language-independent — switch to EN tab to change"
              : "Field key — unique identifier saved as service.setup_responses[<key>]. Letters/digits/_, must start with a letter."
          }
          className={
            "h-7 px-2 text-xs bg-zinc-950 border-zinc-700 font-mono w-[140px] flex-shrink-0 " +
            (isLocalized ? "text-zinc-500" : "text-zinc-50")
          }
        />
        <Select value={q.type} onValueChange={(v) => onChange({ type: v })} disabled={isLocalized}>
          <SelectTrigger
            className="h-7 px-2 text-xs bg-zinc-950 border-zinc-700 text-zinc-50 w-[100px] flex-shrink-0 disabled:opacity-60"
            title={
              isLocalized
                ? "Question type is language-independent — switch to EN tab to change"
                : "Question type — text/textarea/yesno/select/url/file. Drives the input UI in the chat."
            }
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            {types.map((t) => (
              <SelectItem key={t} value={t} className="text-zinc-50 text-xs">{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-0.5 flex-1 min-w-[96px]">
          <Input
            value={view.label}
            onChange={(e) => writeT({ label: e.target.value })}
            placeholder={isLocalized ? `${viewLang.toUpperCase()} short label` : "Short label"}
            title="Short label — shown next to the answer in the chat sidebar (the long question text goes in 'Message' below)"
            className="h-7 px-2 text-xs bg-zinc-950 border-zinc-700 text-zinc-50 flex-1"
          />
          <FallbackDot visible={isLocalized && view.isFallback && !!view.label} />
        </div>
        <label
          className="flex items-center gap-1 text-[10px] text-zinc-300 px-1"
          title={
            isLocalized
              ? "Required setting is language-independent — switch to EN tab to change"
              : q.required
              ? "Required — the chat won't reach 'completed' until the client answers this"
              : "Optional — the client may skip this question"
          }
        >
          <Switch
            checked={!!q.required}
            onCheckedChange={(v) => onChange({ required: v })}
            disabled={isLocalized}
            className="scale-75"
          />
          <span className="flex items-center gap-0.5">
            <Asterisk className={"h-3 w-3 " + (q.required ? "text-red-400" : "text-zinc-500")} />
            <span className="hidden xl:inline">Req</span>
          </span>
        </label>
        <OwnerPicker q={q} onChange={onChange} disabled={isLocalized} />
        <PriorityPicker q={q} onChange={onChange} disabled={isLocalized} />
        <ImproviseToggle q={q} onChange={onChange} disabled={isLocalized} />
        <label
          className="flex items-center gap-1 text-[10px] text-zinc-300 px-1"
          title={
            isLocalized
              ? "Active setting is language-independent — switch to EN tab to change"
              : q.active !== false
              ? "Active — shown to clients in the chat"
              : "Hidden — kept in the schema but the chat never asks it"
          }
        >
          <Switch
            checked={q.active !== false}
            onCheckedChange={(v) => onChange({ active: v })}
            disabled={isLocalized}
            className="scale-75"
          />
          <span className="flex items-center gap-0.5">
            {q.active !== false ? (
              <Eye className="h-3 w-3 text-cyan-400" />
            ) : (
              <EyeOff className="h-3 w-3 text-zinc-500" />
            )}
            <span className="hidden xl:inline">Show</span>
          </span>
        </label>
        <label
          className="flex items-center gap-1 text-[10px] text-zinc-300 px-1"
          title={
            isLocalized
              ? "AI validation is language-independent — switch to EN tab to change"
              : q.ai_validate !== false
              ? "AI validation ON — the bot checks the answer for sense before saving (catches placeholders, mashing, format mismatches) and asks again if it looks wrong"
              : "AI validation OFF — any non-empty answer is accepted as-is"
          }
        >
          <Switch
            checked={q.ai_validate !== false}
            onCheckedChange={(v) => onChange({ ai_validate: v })}
            disabled={isLocalized}
            className="scale-75"
          />
          <span className="flex items-center gap-0.5">
            <Sparkles
              className={
                "h-3 w-3 " + (q.ai_validate !== false ? "text-violet-400" : "text-zinc-500")
              }
            />
            <span className="hidden xl:inline">AI</span>
          </span>
        </label>
        <div className="flex items-center gap-0.5 ml-auto flex-shrink-0">{trBadges}</div>
        <Button size="sm" variant="ghost" onClick={onUp} title="Move up in flow order" className="h-6 w-6 p-0 text-zinc-400">
          <ArrowUp className="h-3 w-3" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onDown} title="Move down in flow order" className="h-6 w-6 p-0 text-zinc-400">
          <ArrowDown className="h-3 w-3" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onRemove} title="Delete this question" className="h-6 w-6 p-0 text-red-400 hover:text-red-300">
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {/* Expanded body — message + placeholder + select options */}
      {expanded && (
        <div className="px-2 pb-2 pt-0 space-y-1.5">
          <div className="space-y-0.5">
            <Textarea
              value={view.message}
              onChange={(e) => writeT({ message: e.target.value })}
              placeholder={
                isLocalized
                  ? `Translate the message into ${viewLang.toUpperCase()}…`
                  : "Question text the AI shows the user…"
              }
              rows={2}
              className="text-xs bg-zinc-950 border-zinc-700 text-zinc-50"
            />
            {isLocalized && view.isFallback && (
              <div className="text-[10px] text-amber-400 italic px-1">
                ↩ falling back to English: <span className="text-zinc-400">{q.message}</span>
              </div>
            )}
            {isLocalized && !view.isFallback && q.message && (
              <div className="text-[10px] text-zinc-500 italic px-1">
                EN: <span className="opacity-80">{q.message}</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="space-y-0.5">
              <Input
                value={view.placeholder}
                onChange={(e) => writeT({ placeholder: e.target.value })}
                placeholder={isLocalized ? `${viewLang.toUpperCase()} placeholder` : "Placeholder hint (optional)"}
                className="h-7 text-xs bg-zinc-950 border-zinc-700 text-zinc-50"
              />
              {isLocalized && q.placeholder && (
                <div className="text-[10px] text-zinc-500 italic px-1 truncate">
                  EN: <span className="opacity-80">{q.placeholder}</span>
                </div>
              )}
            </div>
            {q.type === "select" && (
              <div className="space-y-0.5">
                <Textarea
                  value={(view.options || []).join("\n")}
                  onChange={(e) =>
                    writeT({
                      options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
                    })
                  }
                  rows={2}
                  placeholder={isLocalized ? `One ${viewLang.toUpperCase()} option per line` : "One option per line"}
                  className="text-xs bg-zinc-950 border-zinc-700 text-zinc-50 font-mono"
                />
                {isLocalized && (q.options || []).length > 0 && (
                  <div className="text-[10px] text-zinc-500 italic px-1 truncate">
                    EN: <span className="opacity-80">{(q.options || []).join(" / ")}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Per-question translation editor dialog ────────────────────────────────

function TranslationDialog({ q, code, onClose, onSave }) {
  const langName = TRANSLATABLE_LANGS.find((l) => l.code === code)?.label || code;
  const existing = q.translations?.[code] || {};
  const [draft, setDraft] = useState({
    section: existing.section || "",
    label: existing.label || "",
    message: existing.message || "",
    placeholder: existing.placeholder || "",
    options: existing.options ? existing.options.join("\n") : "",
  });

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Edit {langName} translation —{" "}
            <span className="font-mono text-sm text-cyan-400">{q.field}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FieldPair label="Section" en={q.section} value={draft.section} onChange={(v) => setDraft({ ...draft, section: v })} />
          <FieldPair label="Short label" en={q.label} value={draft.label} onChange={(v) => setDraft({ ...draft, label: v })} />
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Message</Label>
            <div className="text-xs text-zinc-500 italic px-1 pb-1">EN: {q.message}</div>
            <Textarea
              value={draft.message}
              onChange={(e) => setDraft({ ...draft, message: e.target.value })}
              rows={3}
              className="text-sm bg-zinc-950 border-zinc-700 text-zinc-50"
            />
          </div>
          <FieldPair label="Placeholder" en={q.placeholder} value={draft.placeholder} onChange={(v) => setDraft({ ...draft, placeholder: v })} />
          {q.type === "select" && (
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Options (one per line)</Label>
              <div className="text-xs text-zinc-500 italic px-1 pb-1">EN: {(q.options || []).join(" / ")}</div>
              <Textarea
                value={draft.options}
                onChange={(e) => setDraft({ ...draft, options: e.target.value })}
                rows={3}
                className="text-xs bg-zinc-950 border-zinc-700 text-zinc-50 font-mono"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() =>
              onSave({
                section: draft.section || null,
                label: draft.label || null,
                message: draft.message || null,
                placeholder: draft.placeholder || null,
                options:
                  q.type === "select"
                    ? draft.options.split("\n").map((s) => s.trim()).filter(Boolean)
                    : null,
              })
            }
            className="bg-cyan-600 hover:bg-cyan-500 text-white"
          >
            Save translation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldPair({ label, en, value, onChange }) {
  return (
    <div>
      <Label className="text-[10px] uppercase text-zinc-500">{label}</Label>
      <div className="text-xs text-zinc-500 italic px-1 pb-1">EN: {en || <span className="opacity-60">(empty)</span>}</div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 text-sm bg-zinc-950 border-zinc-700 text-zinc-50"
      />
    </div>
  );
}
